import * as React from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  useDisclosure,
  Spacer
} from "@chakra-ui/react";
import { useToast, setErrorsFromResponse } from "duck";

interface ConfirmProps {
  title?: React.ReactNode;
  body?: React.ReactNode;
  confirmText?: React.ReactNode;
  successMessage?: string;
  onConfirm: () => Promise<any>;
  footerExtra?: React.ReactNode | ((onClose: () => void) => React.ReactNode);
  confirmColorScheme?: "blue" | "red";
  children: React.ReactElement;
  minW?: string | number;
}

const Confirm: React.FC<ConfirmProps> = ({
  title,
  body,
  onConfirm,
  successMessage,
  confirmText = "Delete",
  confirmColorScheme = "red",
  children,
  footerExtra,
  minW
}) => {
  const [loading, setLoading] = React.useState(false);

  const cancelRef = React.useRef(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const toast = useToast({
    status: "success"
  });

  const handleConfirm = () => {
    setLoading(true);
    onConfirm()
      .then(() => {
        setLoading(false);
        onClose();
        if (successMessage) {
          toast({ status: "success", description: successMessage });
        }
      })
      .catch(error => {
        setErrorsFromResponse(error);
        setLoading(false);
        throw error;
      });
  };

  return (
    <>
      {React.cloneElement(React.Children.only(children), { onClick: onOpen })}

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent minW={minW}>
            {title && (
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {title}
              </AlertDialogHeader>
            )}

            <AlertDialogBody>{body}</AlertDialogBody>

            <AlertDialogFooter>
              {typeof footerExtra === "function"
                ? footerExtra(onClose)
                : footerExtra}

              {!!footerExtra && <Spacer />}

              <Button colorScheme="gray" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme={confirmColorScheme}
                onClick={handleConfirm}
                ml={3}
                isLoading={loading}
              >
                {confirmText}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default Confirm;
